import { useCustomViewBlockContext } from '@lighthouse/block'
import type { FloatBlockAbstract } from '@lighthouse/core'
import { useAtomAction, useAtomData, useFloatBoxBlockContext, usePageContainer } from '@lighthouse/shared'
import { findLast, mergeDeepRight } from 'rambda'
import { useCallback, useMemo, useRef } from 'react'

import { toggleFloatBoxAtom } from '@/atoms/application/action'
import { pageStackOfFloatBlockAtom } from '@/atoms/application/state'
import { useCurrentStackIdContext } from '@/contexts/PageContext'

import { useDeviceType } from './useApplication'

export function useFloatBlock(floatBlockList: FloatBlockAbstract[], scope?: string) {
    const stackId = useCurrentStackIdContext()
    const { dragPlugin } = usePageContainer()
    const previewType = useDeviceType()
    const targetRef = useRef<HTMLDivElement>(null)
    const { id: parentFloatId } = useFloatBoxBlockContext()
    const { record: viewRecord } = useCustomViewBlockContext()

    const [openedMap, floatBlocks]: [Record<string, boolean>, FloatBlockAbstract[]] = useAtomData(
        pageStackOfFloatBlockAtom,
        useCallback(
            v => {
                const currentFloatBoxBlock = floatBlockList.find(block => block.id === v.at(-1)?.blockId)
                const floatBlock = currentFloatBoxBlock ?? floatBlockList[0]
                const newFloatBlock = floatBlock
                    ? mergeDeepRight<FloatBlockAbstract>(floatBlock, {
                          config: {
                              breakPoint: {
                                  overlay: {
                                      showOn: 'click',
                                      // 关闭时机
                                      dismiss: 'click'
                                  }
                              }
                          }
                      })
                    : undefined

                return [
                    Object.fromEntries(
                        (floatBlockList ?? []).map(({ id: blockId }) => {
                            return [
                                blockId,
                                findLast(
                                    d =>
                                        d.blockId === blockId &&
                                        stackId === d.stackId &&
                                        (scope === undefined || scope === d.scope) &&
                                        (viewRecord === undefined || viewRecord?.id === d.recordId),
                                    v
                                )?.open ?? false
                            ]
                        })
                    ),
                    newFloatBlock ? [newFloatBlock] : []
                ]
            },
            [floatBlockList, scope, stackId, viewRecord]
        )
    )

    const { run: openFloat } = useAtomAction(toggleFloatBoxAtom)

    const handleOpenChange = useCallback(
        (v: boolean, blockId: string) => {
            openFloat({
                stackId,
                blockId,
                scope,
                recordId: viewRecord?.id,
                open: v
            })
        },
        [openFloat, scope, stackId, viewRecord]
    )

    return useMemo(
        () => ({
            openedMap,
            floatBlocks,
            parentFloatId,
            targetRef,
            isOpenToggle: false,
            previewType,
            updateDragNode: () => {
                dragPlugin?.collectNodes()
            },
            onOpenChange: handleOpenChange
        }),
        [openedMap, floatBlocks, parentFloatId, previewType, handleOpenChange, dragPlugin]
    )
}
